import type { ApiEnvelope } from '~/types/types'
import type { PublicPaymentMethod } from '~/repositories/paymentMethodRepository'

/**
 * Public marketplace endpoints used by checkout flows on tenant landing
 * pages. The booking page (and other paid features on the tenant subdomain)
 * relies on this to render PaymentDialog with the methods that the tenant
 * has actually configured.
 *
 * Platform-level methods (used for tenant subscription upgrades) are still
 * a stub — agency-saas hasn't shipped that flow yet.
 */
export const useMarketplaceRepository = () => {
  const api = useApi()
  const tenant = useTenant()

  const getTenantPaymentMethods = async (slug?: string): Promise<PublicPaymentMethod[]> => {
    const headers: Record<string, string> = {}
    const resolvedSlug = slug ?? tenant.routeSlug.value ?? tenant.tenant.value?.slug
    if (resolvedSlug) headers['X-Tenant-Slug'] = resolvedSlug
    try {
      const res = await api.get<ApiEnvelope<PublicPaymentMethod[]>>(
        '/api/public/tenant/payment-methods',
        { headers },
      )
      return res.data
    } catch {
      return []
    }
  }

  const getPublicPlatformPaymentMethods = async (): Promise<PublicPaymentMethod[]> => {
    if (import.meta.dev) {
      // eslint-disable-next-line no-console
      console.warn('[useMarketplaceRepository] getPublicPlatformPaymentMethods() — agency-saas has no platform marketplace yet.')
    }
    return []
  }

  return { getTenantPaymentMethods, getPublicPlatformPaymentMethods }
}
